import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/Layout';
import { getRecipientRequirements } from '../../store/sagas/api';
import ErrorMessage from '../../components/ErrorMessage';
import AddRecipientWizardShell, { WizardFormFooter } from '../../components/AddRecipientWizard';

function SelectRail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currency = searchParams.get('currency');
  const recipientType = searchParams.get('recipientType');
  const autoPopulate = searchParams.get('autoPopulate') === 'true';

  const { currencies: configCurrencies } = useSelector((state) => state.config);
  const [rails, setRails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCountryFromCurrency = (currencyCode) => {
    if (!currencyCode || !configCurrencies || configCurrencies.length === 0) return null;

    const normalizedCurrency = currencyCode.toUpperCase();
    const currencyConfig = configCurrencies.find((c) => c.code === normalizedCurrency);
    if (!currencyConfig) return null;

    if (currencyConfig.country && currencyConfig.country.code) {
      return currencyConfig.country.code;
    }

    if (currencyConfig.countries && Array.isArray(currencyConfig.countries) && currencyConfig.countries.length > 0) {
      return currencyConfig.countries[0].code;
    }

    return null;
  };

  const extractRails = (data, recipientTypeLower) => {
    if (!data || !data.rails || !Array.isArray(data.rails)) return [];

    const railList = [];
    data.rails.forEach((rail) => {
      if (rail.name && rail.types && Array.isArray(rail.types) && rail.types.includes(recipientTypeLower)) {
        railList.push(rail.name);
      }
    });
    return railList;
  };

  useEffect(() => {
    if (!currency || !recipientType) {
      navigate('/select-currency');
      return;
    }

    const bankCountryCode = getCountryFromCurrency(currency);
    setError(null);

    getRecipientRequirements(currency, bankCountryCode)
      .then((data) => {
        const recipientTypeLower = recipientType === 'individual' ? 'individual' : 'company';
        const uniqueRails = extractRails(data, recipientTypeLower);

        if (uniqueRails.length === 1) {
          const params = new URLSearchParams({
            currency,
            recipientType,
            rail: uniqueRails[0],
          });
          if (autoPopulate) {
            params.set('autoPopulate', 'true');
          }
          navigate(`/add-recipient-form?${params.toString()}`, { replace: true });
          return;
        }

        if (uniqueRails.length > 0) {
          setRails(uniqueRails);
          setLoading(false);
        } else {
          setRails([]);
          setLoading(false);
        }
      })
      .catch((fetchError) => {
        setError(fetchError);
        setRails([]);
        setLoading(false);
      });
  }, [currency, recipientType, configCurrencies, navigate, autoPopulate]);

  const handleBack = () => {
    navigate(`/recipient-type?currency=${currency}`);
  };

  const handleCancel = () => {
    navigate('/recipients');
  };

  const handleRailSelect = (selectedRail) => {
    const params = new URLSearchParams({
      currency,
      recipientType,
      rail: selectedRail,
      fromRailPicker: 'true',
    });
    if (autoPopulate) {
      params.set('autoPopulate', 'true');
    }
    navigate(`/add-recipient-form?${params.toString()}`);
  };

  const getRailDisplayName = (rail) => {
    if (!rail || typeof rail !== 'string') return rail;
    return rail
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Layout
      hideHeader
      mainPaddingClassName='px-6 py-9 sm:px-11'
      floatBack={{ onClick: handleBack, label: 'Back', ariaLabel: 'Back to recipient type' }}
    >
      <AddRecipientWizardShell
        currentStep={2}
        title='Select transfer method'
        subtitle='Choose how you want to send to this recipient.'
        footer={<WizardFormFooter onCancel={handleCancel} showContinue={false} />}
      >
        {error && (
          <div className='mb-4'>
            <ErrorMessage error={error} title='Currency not available' />
          </div>
        )}

        {loading && (
          <div className='py-8 text-center'>
            <p className='text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>Loading transfer methods...</p>
          </div>
        )}

        {!loading && rails.length > 0 && (
          <div className='flex flex-col gap-1'>
            {rails.map((rail) => (
              <button
                key={rail}
                type='button'
                onClick={() => handleRailSelect(rail)}
                className='flex w-full cursor-pointer items-center gap-3.5 rounded-[10px] border border-transparent px-3.5 py-3 text-left transition-colors hover:border-[var(--color-border)] hover:bg-[var(--off)]'
              >
                <div className='min-w-0 flex-1'>
                  <div className='text-sm font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>{getRailDisplayName(rail)}</div>
                </div>
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='var(--text-tertiary)' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <path d='M9 18l6-6-6-6' />
                </svg>
              </button>
            ))}
          </div>
        )}

        {!loading && rails.length === 0 && !error && (
          <div className='py-8 text-center'>
            <p className='text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>No transfer methods available.</p>
          </div>
        )}
      </AddRecipientWizardShell>
    </Layout>
  );
}

export default SelectRail;
